import { attach, combine, createStore, sample } from 'effector'
import keyBy from 'lodash/keyBy'

import { persistProfileData } from '@/entities/profile/portal'
import { constants } from '@/shared/config'

import * as api from '../api'
import { addHistory } from './lib'
import * as connModel from './model.conn'
import * as userModel from './model.user'

export const $chatChannel = createStore<string>(constants.CHAT_CHANNEL_DEFAULT)
export const $chatChannelItems = combine(connModel.$chatItems, $chatChannel, (items, channel) => items[channel] || [])
export const $chatChannelMessageById = combine(connModel.$chatItems, $chatChannel, (items, channel) => {
    const channelItems = items[channel] || []
    const messages = channelItems.flatMap((item) => (item.type === 'message' ? [item.payload] : []))

    return keyBy(messages, (message) => message.id)
})

// store current channel in local storage to persist it between sessions
persistProfileData({
    store: $chatChannel,
    key: 'chat.channel',
})

export const $chatChannelList = createStore<api.ChatChannel[]>([])
    .on(connModel.channelSubscribed, (state, { channel }) => {
        if (state.some((chan) => chan.name === channel)) {
            return state
        }
        return [...state, { name: channel }]
    })
    .on(connModel.channelUnsubscribed, (state, { channel }) => state.filter((chan) => chan.name !== channel))

export const $chatAdmins = createStore<Record<string, api.ChatAdmin>>({})

export const channelHistoryFx = attach({ effect: api.chatapi.channelHistory })

// switch to the default channel if the current channel is not in the list of joined channels
sample({
    clock: $chatChannelList,
    source: $chatChannel,
    filter: (channel, joined) => joined.length > 0 && !joined.some((chan) => chan.name === channel),
    fn: (_channel, joined) => joined[0]?.name ?? constants.CHAT_CHANNEL_DEFAULT,
    target: $chatChannel,
})

// --- Channel history ---

sample({
    clock: connModel.channelSubscribed,
    fn: (subscription): api.HistoryRequest => ({
        channel: subscription.channel,
        limit: 1000,
    }),
    target: channelHistoryFx,
})

sample({
    clock: channelHistoryFx.done,
    source: connModel.$chatItems,
    fn: (events, { params: { channel }, result: history }) => ({
        ...events,
        [channel]: addHistory(events[channel], history.messages),
    }),
    target: connModel.$chatItems,
})

// -- Chat admins ---

sample({
    clock: userModel.loginFx.doneData,
    fn: (response) => keyBy(response.chat_admins, (admin) => admin.user_id),
    target: $chatAdmins,
})

// -- Chat channels ---

sample({
    clock: userModel.loginFx.doneData,
    fn: (response) =>
        response.channels
            // system channels are not shown in the list
            .filter((channel) => !channel.name.startsWith('system:')),
    target: $chatChannelList,
})
